import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Sparkles, ArrowLeft, Eye, EyeOff, Check } from 'lucide-react';
import { motion } from 'motion/react';
import { Box, Typography, Stack, Card, Link as MuiLink, Alert } from '@mui/material';
import { supabase } from '../lib/supabase';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Validation rules
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password || password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (!hasMinLength || !hasNumber || !hasSpecialChar) {
            setError('Password does not meet requirements');
            return;
        }
        setError(null);

        setIsLoading(true);
        try {
            const { error: updateError } = await supabase.auth.updateUser({
                password: password
            });
            if (updateError) throw updateError;
            navigate('/auth/email-verification-success');
        } catch (err: any) {
            setError(err.message || 'Failed to update password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
            {/* Top Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: { xs: 3, md: 6 },
                py: 3,
                bgcolor: '#f8f9fa',
            }}>
                <Stack direction="row" alignItems="center" spacing={1} component={Link} to="/" sx={{ textDecoration: 'none' }}>
                    <Box sx={{ bgcolor: '#4f46e5', p: 0.5, borderRadius: 1.5, display: 'flex' }}>
                        <Sparkles color="white" size={24} />
                    </Box>
                    <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em" sx={{ color: '#111827', fontSize: '1.2rem' }}>
                        PostPilot AI
                    </Typography>
                </Stack>

                {/* Empty Box acting as the right-side avatar placeholder shown in the mockup */}
                <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ width: 16, height: 16, border: '2px solid #94a3b8', borderRadius: 0.5 }} />
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{ width: '100%', maxWidth: 460 }}
                >
                    <Card elevation={0} sx={{ p: { xs: 4, sm: 5 }, borderRadius: 4, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', bgcolor: 'white' }}>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h5" fontWeight={800} sx={{ color: '#111827', mb: 1, letterSpacing: '-0.02em' }}>
                                Reset Password
                            </Typography>
                            <Typography variant="body2" color="#475569" sx={{ lineHeight: 1.6 }}>
                                Step 2: Enter your new secure password below to regain access.
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="body2" fontWeight={700} color="#334155" sx={{ mb: 1 }}>New Password</Typography>
                                    <Box sx={{ position: 'relative' }}>
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Enter new password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            sx={{ pr: 5 }}
                                        />
                                        <Box
                                            onClick={() => setShowPassword(!showPassword)}
                                            sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}
                                        >
                                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="body2" fontWeight={700} color="#334155" sx={{ mb: 1 }}>Confirm New Password</Typography>
                                    <Box sx={{ position: 'relative' }}>
                                        <Input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="Repeat new password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            sx={{ pr: 5 }}
                                        />
                                        <Box
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}
                                        >
                                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Password Requirements */}
                                <Box sx={{ bgcolor: '#f8fafc', p: 3, borderRadius: 2 }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ color: '#64748b', display: 'block', mb: 2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                        Password Requirements
                                    </Typography>
                                    <Stack spacing={1.5}>
                                        <RequirementItem met={hasMinLength} text="At least 8 characters" />
                                        <RequirementItem met={hasNumber} text="Includes a number" />
                                        <RequirementItem met={hasSpecialChar} text="Includes a special character" />
                                    </Stack>
                                </Box>

                                <Button type="submit" size="lg" sx={{ width: '100%', bgcolor: '#4f46e5', color: 'white', '&:hover': { bgcolor: '#4338ca' }, py: 1.5, fontWeight: 'bold', fontSize: '1rem', mt: 3 }} isLoading={isLoading}>
                                    Update Password
                                </Button>
                            </Stack>
                        </form>

                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                            <MuiLink component={Link} to="/auth" variant="body2" fontWeight={600} sx={{ color: '#64748b', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { color: '#475569' } }}>
                                <ArrowLeft size={16} /> Back to Sign In
                            </MuiLink>
                        </Box>

                    </Card>
                </motion.div>
            </Box>

            {/* Footer */}
            <Box component="footer" sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="caption" fontWeight="medium" color="#94a3b8" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <span>© {new Date().getFullYear()} PostPilot AI. All rights reserved. Secure password management powered by PostPilot Systems.</span>
                </Typography>
            </Box>
        </Box>
    );
}

function RequirementItem({ met, text }: { met: boolean; text: string }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
                width: 18,
                height: 18,
                borderRadius: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: met ? '#4f46e5' : 'transparent',
                border: `1px solid ${met ? '#4f46e5' : '#cbd5e1'}`,
                color: 'white',
                transition: 'all 0.2s'
            }}>
                {met && <Check size={14} strokeWidth={3} />}
            </Box>
            <Typography variant="body2" sx={{ color: met ? '#475569' : '#94a3b8', fontWeight: met ? 500 : 400 }}>
                {text}
            </Typography>
        </Box>
    );
}
